import React, {Fragment, memo, useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import ReactModal from 'react-modal';
import {joiResolver} from '@hookform/resolvers/joi';
import {FormProvider, useFieldArray, useForm} from 'react-hook-form';
import _debounce from 'lodash/debounce';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import moment from 'moment';

import {ActionButton, ConfirmModal} from '../../../common';
import {puritiesSchemaFunc} from './partialQuenchingModalSchema';
import {DSelect, HCheckbox, HDatePicker, HInput, HTextarea} from '../../../../validation/HForm';
import {resetSearch, searchJewels} from '../../../../redux/actions/searchAction';
import { getClientLastDebtLoad, getDebtLoadGroupRequest } from '../../../../redux/actions/debtLoadAction';
import { debtHandbookFields } from '../../../DebtToIncomeRatio/helpers';
import DeleteIcon from '../../../../assets/icons/trash.svg';
import {requestForCalculation, resetCalculations} from '../../../../redux/actions/quickCalculationAction';
import {searchService} from '../../../../services/search-service';
import {
    clearModalError,
    clearPartialQuenchingData,
    contractPartialQuenching,
    getPrintedForm
} from '../../../../redux/actions/contractAction';
import {
    dealTypes,
    debtNotificationError,
    paymentErrors,
    viewReports,
    waitingMessage,
} from '../../../../helpers/data-constants';
import { useDebounce } from '../../../../helpers/hooks';
import {Message} from '../../../utilites';
import Loader from './Loader/Loader';
import PaymentTypeSelector from '../../../utilites/PaymentSelector/PaymentTypeSelector';
import { DebtLoadDataForm, DebtResults } from '../../../common/DebtLoadCalculation';
import { useDebtLoadCalculation } from '../../../common/DebtLoadCalculation/useDebtLoadCalculation.hook';
import './partialQuenchingModal.scss';
import BasicAlert from "../../../utilites/Alert/Alert";
import {config} from "../../../../helpers/config";

const PartialQuenchingModal = memo(({isOpen, clientContract, handleCloseModal, type, initValues}) => {
    const {commonError} = useSelector(({search}) => search);
    const {accessFeatures} = useSelector(({user}) => user);
    const {calculations, commonError: calculationError, loading} = useSelector(
        ({quickCalculation}) => quickCalculation,
    );

    const {
        modalError,
        isModalLoading,
        isModalResponse,
        partialQuenchingData: {nextContractNumber, purityWithPrices, coupons, operationWithMoney}
    } = useSelector(({contract}) => contract);
    const {client} = useSelector(({clientInfo}) => clientInfo);
    const { debtLoadCalculation, debtCalculateError, debtLoadCalculationLoading } = useDebtLoadCalculation(client.id, calculations, clientContract.dealId);
    const [state, setState] = useState(_cloneDeep(clientContract));
    const [charCounts, setCharCounts] = useState({})
    const [isDebtLoadData, setIsDebtLoadData] = useState(false);
    const isDebtLoadCalculation = calculations.calculateDebtLoad
    const isDebtCalculateResult = (calculations.calculateDebtLoad && debtLoadCalculation?.debtOverloadRate) || debtCalculateError

    const couponNotFind = {
        "value": '',
        "label": '',
        "maxDuration": '',
        "minDuration": '',
        "type": [],
    }
    const filterCoupons = coupons.filter((c) => c.category !== 10)
    const initialCouponIndx = coupons.findIndex((element) => clientContract.couponId === element.value);
    const initialCoupon = initialCouponIndx === -1 ? couponNotFind : coupons[initialCouponIndx];
    const minDuration = state?.coupon?.minDuration || initialCoupon.minDuration;
    const maxDuration = state?.coupon?.maxDuration || initialCoupon.maxDuration;
    const [errorModalState, setErrorModalState] = useState(false);
    const [basicAlertMessage, setBasicAlertMessage] = useState('')
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(loading)
    const [paymentInputs, setPaymentInputs] = useState([])
    const typeOperation = operationWithMoney === 'Выдать деньги';

    const minDate =
        minDuration !== null
            ? minDuration === 0
                ? moment().add(minDuration, 'days').format('YYYY-MM-DD')
                : moment()
                    .add(minDuration - 1, 'days')
                    .format('YYYY-MM-DD')
            : moment().add(2, 'days').format('YYYY-MM-DD');

    const maxDate =
        maxDuration
            ? moment()
                .add(maxDuration - 1, 'days')
                .format('YYYY-MM-DD')
            : moment().add(28, 'days').format('YYYY-MM-DD');

    const [confirmOpen, setConfirmOpen] = useState({
        isDismissButton: false,
        isApplyButton: false,
        data: {},
    });
    const [jewel, setJewel] = useState([]);
    const [part, setPart] = useState([]);
    const history = useHistory();
    const dispatch = useDispatch();
    const notEmpty = state.positions && state.positions.length !== 0;
    const combineError = commonError || calculationError || modalError;
    const isDisabled = calculations.availableAmount < 0 || calculationError || isSubmitDisabled;
    const buttonStyle = isDisabled ? "button-light" : "button";
    const isNonCashOperationCrediton = accessFeatures['non_cash.operations.type']

    const defaultItem = {
        names: [],
        comment: '',
        estimatedValue: 0,
        grossWeight: 0,
        pureWeight: 0,
        purityId: 0,
        purityIdNamePrice: {value: '', label: '', price: ''},
        purityPrice: 0,
        purityType: '',
        quantity: 1,
        showComment: false,
        isPart: false,
        additionalInfo: '',

    }

    const filterNames = (arr) => {
        const filterArr = arr.filter(n => n !== 'ч.' && n !== 'б/вст' && n !== 'с/вст' && n !== '/вставка')
        if (filterArr?.length > 1) {
            return {label: filterArr.join(','), value: 0}
        }
        const filterJewel = jewel && jewel.filter(j => j.label === filterArr[0] || j.part === filterArr[0])
            .map((i) => {
                    return {value: i.value, label: i.label}
                }
            )
        return {value: filterJewel[0]?.value, label: filterArr[0]}
    }

    const form = useForm({
        mode: 'all',
        defaultValues: {
            positions:
                notEmpty &&
                state.positions.map((position) => {
                    const initialPurityPrice = purityWithPrices ? purityWithPrices.find(element => element.value === position.purityId) : {prise: 0}
                    return {
                        comment: position.comment,
                        grossWeight: position.grossWeight,
                        names: filterNames(position.names),
                        pureWeight: position.pureWeight,
                        quantity: position.quantity,
                        positionId: position.positionId,
                        purityId: position.purityId,
                        purityPrice: initialPurityPrice?.price || 0,
                        purityType: position.purityType,
                        isPart: position.names.includes('ч.'),
                        additionalInfo: position.additionalInfo ? position.additionalInfo : '',
                        showComment: !!position.comment
                    }
                }),
        },
        resolver: joiResolver(puritiesSchemaFunc(config().validatePurities)),
    });
    const fieldArray = useFieldArray({
        control: form.control,
        name: 'positions',
        keyName: 'positionIds',
    });

    const getValue = (path1, path2) => {
        return (form.watch(path1) ?? form.watch(path2)) || 0;
      };

    const cash = getValue('calculation.deposit.cash', 'deposit.cash');
    const nonCash = getValue('calculation.deposit.nonCash', 'deposit.nonCash');
    const nonCashAccount = form.watch('calculation.deposit.nonCashAccount') || 0;
    const bankTransaction = getValue('calculation.deposit.bankTransaction', 'deposit.bankTransaction');
    const total = +cash + +nonCash + +nonCashAccount + +bankTransaction;

    useEffect(() => {
        const purities = clientContract.positions.map(item => {
            return {
                grossWeight: item.grossWeight,
                pureWeight: item.pureWeight,
                purityId: item.purityId,
                purityQuantity: item.quantity
            }
        });
        const req = {
            couponId: clientContract.couponId,
            type: 'PURITY',
            amount: total,
            date: clientContract.endDate,
            isTicketLost: false,
            purities: purities,
            estimatedValue: '',
            minDuration: minDuration,
            maxDuration: maxDuration,
            partialRepayment: typeOperation ? 'DEBIT' : 'CREDIT',
            prevDealId: state.dealId,
            waiverOfRedemption: false,
        };
        dispatch(requestForCalculation(req));
    }, []);

    useEffect(() => {
        searchService.searchJewels('', 1000).then((response) => {
            setJewel(
                response.data.results.map((v) => ({
                    value: parseInt(v.id),
                    label: v.name,
                    part: v.part,
                })),
            );
            setPart(
                response.data.results.map((v) => ({
                    value: parseInt(v.id),
                    label: v.part,
                })),
            );
        });
    }, []);


    useEffect(() => {
        if (calculations && calculations.estimatedValue.length) {
            setState((prev) => ({
                ...prev,
                positions: prev.positions.map((i, ind) => ({
                    ...i,
                    estimatedValue: calculations.estimatedValue[ind],
                })),
                totalEstimatedValue: +calculations.totalEstimatedValue.toFixed(2),
            }));
        }
    }, [calculations]);

    useEffect(() => {
        return () => {
            dispatch(clearModalError());
            dispatch(resetCalculations());
            dispatch(clearPartialQuenchingData());
            dispatch(resetSearch());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isModalResponse && isModalResponse.dealId) {
            const startDate = moment().utc().format('YYYY-MM-DD')
            const filename = `exp_${isModalResponse.dealNumber}_${startDate}_${confirmOpen.data.endDate}.pdf`
            dispatch(
                getPrintedForm(
                    {
                        dealId: isModalResponse.dealId,
                        dealType: 'purity',
                        cashBoxId: confirmOpen.data.cashBoxId,
                    },
                    filename,
                    viewReports.PRINTED_DEAL,
                ),
            )
            handleCloseModal('isPartialQuenching', true);
            history.push(`/client/${client.id}/contracts/${isModalResponse.dealId}`);
        }
    }, [isModalResponse, handleCloseModal, dispatch, history, client.id]);

    useEffect(() => {
        setIsSubmitDisabled(loading);
        if (!loading) setErrorModalState(false);
      }, [loading]);

    const handleCouponChange = () => {
        const data = form.getValues();
        setState((prev) => ({
            ...prev,
            coupon: data.coupon
        }));
        handleCalculating()
    }

    const handlePureWeight = (idx, decimalPlaces) => {
        const data = form.getValues().positions[idx];
        const pureWeight = data.pureWeight;
        const grossWeight = data.grossWeight;
        const newGrossWeight = +pureWeight > +grossWeight ? pureWeight : grossWeight;

        form.setValue(`positions[${idx}].grossWeight`, newGrossWeight, {shouldValidate: true});
        if (+pureWeight < 0
            && +grossWeight > 0
            && (+grossWeight) >= (+pureWeight)) {
            let newPureWeight = ((+grossWeight) + (+pureWeight)).toFixed(decimalPlaces).replace(/\.?0+$/, "")
            newPureWeight = newPureWeight > 0 ? newPureWeight : 0
            form.setValue(`positions[${idx}].pureWeight`, newPureWeight, {shouldValidate: true});
        }
        handleCalculating()
    };
    const handlePurityIdNamePriceChange = (idx) => {
        const data = form.getValues();

        const purity = data.positions[idx].purityIdNamePrice;
        let grossWeight = data.positions[idx].grossWeight;
        let pureWeight = data.positions[idx].pureWeight;
        if (purity) {
            if (purity?.label.startsWith('Серебро')
                && (grossWeight.lastIndexOf('.') !== (grossWeight.length - 2))
                && (grossWeight.lastIndexOf('.') !== -1)
            ) {
                grossWeight = grossWeight.slice(0, grossWeight.length - 1)
                form.setValue(`positions[${idx}].grossWeight`, grossWeight, {shouldValidate: true});
            }
            if (purity?.label.startsWith('Серебро')
                && (pureWeight.lastIndexOf('.') !== (pureWeight.length - 2))
                && (pureWeight.lastIndexOf('.') !== -1)
            ) {
                pureWeight = pureWeight.slice(0, pureWeight.length - 1)
                form.setValue(`positions[${idx}].pureWeight`, pureWeight, {shouldValidate: true});
            }

            fieldArray.fields[idx].purityPrice = purity.price
            fieldArray.fields[idx].purityType = purity.label

            setState((prev) => ({
                ...prev,
                positions: prev.positions.map((position, i) => {
                    if (i === idx) {
                        return {
                            ...position,
                            purityPrice: purity.price,
                            purityType: purity.label,
                            purityId: purity.value,
                            grossWeight: grossWeight,
                            pureWeight: pureWeight,
                        }
                    }
                    return position;
                }),
            }));
            handleCalculating()
        }
    }

    const addPurityRow = () => {
        fieldArray.append(defaultItem, false);
        let pos = [...state.positions];
        pos.push(defaultItem)
        setState((prev) => ({
            ...prev,
            positions: pos,
        }));
    };

    const checkValidLength = () => {
        if (accessFeatures['purities.count_contracts']) {
            if (fieldArray.fields.length < 10) {
                addPurityRow()
            } else {
                setBasicAlertMessage("Максимальное количество ценностей 10")
                setErrorModalState(true);
            }
        } else {
            addPurityRow()
        }
    }

    const removePurityRow = (index) => {
        fieldArray.remove(index);
        let pos = [...state.positions];
        pos.splice(index, 1);
        setState((prev) => ({
            ...prev,
            positions: pos,
        }));
        handleCalculating()
    };

    const handleChangeContent = () => {
        const newPositions = form.getValues().positions;
        form.setValue('positions', newPositions);
    };

    const handlePart = (idx) => {
        if (idx !== undefined) {
            const data = form.getValues().positions[idx];
            const newIsPart = data.isPart;
            const newJewel = jewel?.filter(jewel => jewel.label === data.names.label || jewel.part === data.names.label).map(j => {
                if (newIsPart) {
                    return {value: j.value, label: j.part}
                }
                return {value: j.value, label: j.label}
            })
            form.setValue(`positions[${idx}].names`, newJewel[0], {shouldValidate: true});
            handleChangeContent()
        }
    }
    const handleGrossWeight = (idx) => {
        const data = form.getValues();
        const newPureWeight = data.positions[idx].grossWeight;

        form.setValue(`positions[${idx}].pureWeight`, newPureWeight, {shouldValidate: true});

        handlePurityIdNamePriceChange(idx)
    };

    const showError = (message) => {
        setBasicAlertMessage(message);
        setErrorModalState(true);
        return false;
      };
      
    const validateInputs = () => {
        if (!paymentInputs.length && !typeOperation && isNonCashOperationCrediton) {
          return showError(paymentErrors.PAYMENT_TYPE_REQUIRED);
        }
        if (total <= 0) {
          return showError(paymentErrors.PARTIAL_REPAYMENT_ERROR);
        }
        if (calculations.calculateDebtLoad && !debtLoadCalculation) {
          return showError(debtNotificationError);
        }
        return true;
    };
      
    const handleOpenDebtLoadModal = () => {
        if (total <= 0) {
          return showError(paymentErrors.PARTIAL_REPAYMENT_ERROR);
        }
        
        setIsDebtLoadData(true);
        setErrorModalState(false);
        dispatch(getClientLastDebtLoad(client.id));
        dispatch(getDebtLoadGroupRequest(debtHandbookFields.AVG_INDUSTRY_INCOME))
    };

    const onSubmit = (values) => {
        if(!validateInputs()) return

        const reqPositions = values.positions.map(i => {

            return {
                type: 'PURITY',
                names: [i.names.label],
                isPart: i.isPart,
                quantity: i.quantity,
                comment: i.comment,
                additionalInfo: accessFeatures['purities.additional_info'] ? i.additionalInfo : '',
                purityId: i.purityIdNamePrice.value,
                grossWeight: i.grossWeight,
                pureWeight: i.pureWeight
            }
        })

        const endDate = moment(values.calculation.endDate).utc().format('YYYY-MM-DD');
        const data = {
            checksum: calculations.checksum,
            repaymentChecksum: calculations.repaymentChecksum,
            positions: reqPositions,
            endDate: endDate,
            amount: calculations.amount,
            clientId: client.id,
            couponId: values.coupon.value,
            waiverOfRedemption: false,
            cashBoxId: state.cashBoxId,
            prevDealId: state.dealId,
            partialRepayment: operationWithMoney.startsWith('Выдать') ? 'DEBIT' : 'CREDIT',
            ticketLost: accessFeatures["ticketLost"] ? values.calculation.ticketLost : false,
            deposit: {
                cash: +cash,
                nonCash: +nonCash,
                nonCashAccount: +nonCashAccount,
                bankTransaction: +bankTransaction
            },
        }
        setConfirmOpen((prev) => ({...prev, isApplyButton: true, data}));
    };

    const handleJewelChange = (val, action) => {
        if (action.action === 'input-change') {
            dispatch(searchJewels(val, 10));
        }
    };

    const showCalculatingMessage = () => {
        setIsSubmitDisabled(true);
        setBasicAlertMessage(waitingMessage);
        setErrorModalState(true);
    };
    
    const handleSumChange = (e) => {
        const newValue = +e.currentTarget.value;
        if (total === debouncedValue || newValue <= 0) {
            setIsSubmitDisabled(false);
            setErrorModalState(false);
        } else {
            showCalculatingMessage();
        }
    };
 
    const handleCalculating = () => {
        showCalculatingMessage();
        debounce();
    };

    const handleChange = () => {
        const {calculation, coupon, positions} = form.getValues();
        const purities = positions.map(item => {
            return {
                grossWeight: item.grossWeight,
                pureWeight: item.pureWeight,
                purityId: +item.purityIdNamePrice?.value,
                purityQuantity: item.quantity
            }
        });
        const req = {
            clientId: client.id,
            couponId: coupon.value,
            type: 'PURITY',
            amount: total,
            date: calculation.endDate,
            isTicketLost: !!calculation.ticketLost,
            purities: purities,
            estimatedValue: '',
            minDuration: coupon.minDuration,
            maxDuration: coupon.maxDuration,
            partialRepayment: typeOperation ? 'DEBIT' : 'CREDIT',
            prevDealId: state.dealId,
            waiverOfRedemption: false,
            dealForm: dealTypes.SHORT_TERM.dealForm
        };
        dispatch(requestForCalculation(req));
        dispatch(clearModalError())
    };

    const debouncedValue = useDebounce(total, 1000, handleChange)
    const debounce = _debounce(handleChange, 1000);

    const handleOpenClose = useCallback(() => {
        const data = form.getValues();
        data.positions = data.positions.map((i) => ({
            comment: i.comment,
            grossWeight: +i.grossWeight,
            names: i.names,
            positionId: +i.positionId,
            pureWeight: +i.pureWeight,
            purityId: +i.purityId,
            quantity: +i.quantity,
        }))
        if (_isEqual(initValues, data)) {
            handleCloseModal('isPartialQuenching');
            dispatch(clearModalError());
        } else {
            setConfirmOpen((prev) => ({...prev, isDismissButton: true}));
        }
    }, [form, handleCloseModal, initValues, dispatch]);

    const handleLocalCloseModal = useCallback(() => {
        setConfirmOpen((prev) => ({...prev, isDismissButton: false, isApplyButton: false}));
    }, []);

    const closePopUp = useCallback(() => {
        handleCloseModal('isPartialQuenching');
        setConfirmOpen((prev) => ({...prev, isDismissButton: false, isApplyButton: false}));
    }, [handleCloseModal]);

    const applyChanges = useCallback(() => {
            setConfirmOpen((prev) => ({...prev, isDismissButton: false, isApplyButton: false}));
    
            if(!combineError) {
                dispatch(contractPartialQuenching(confirmOpen.data));
            }
    }, [confirmOpen.data, combineError, dispatch]);

    const handleCharCountChange = (idx) => {
        if (!accessFeatures['count_additionalInfo_symbols']) {
            return;
        }

        const data = form.getValues();
        let additionalInfo = data.positions[idx].additionalInfo;

        let charCount = additionalInfo.length > 0 ? additionalInfo.length : 0;

        setCharCounts((prevCharCounts) => ({
            ...prevCharCounts,
            [idx]: charCount,
        }));
    };

    const handleCloseDebtLoadModal = () => {
        setIsDebtLoadData(false);
      };

    const modalHeaderText = () => {
        if (debtLoadCalculation?.isDebtOverloaded) {
            return `Значение ПДН превышает 40%. Вы уверены что хотите выполнить операцию "${operationWithMoney}"?`;
        }
        return `Выполнить операцию "${operationWithMoney}"?`;
    };

    return (
        <ReactModal
            isOpen={isOpen}
            contentLabel="Modal"
            className="modal-container editModal-modal-container"
            overlayClassName="modal-overlay">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    {combineError && <Message type="error" text={combineError}/>}
                    <div className="modal-header editModal-modal-header">
                        <h2>
                            {`Изменение суммы договора ${state.dealNumber} -> ${nextContractNumber.dealNumber} `}
                            <span className={
                                typeOperation
                                    ? 'title-itemBackground__orange'
                                    : 'title-itemBackground__blue'}
                            >
                {`${operationWithMoney}`}
              </span>
                        </h2>
                    </div>
                    {nextContractNumber.lastDealNumber && <div className="modal-header editModal-modal-subheader">
                        {`Предыдущий ${nextContractNumber.lastDealNumber} от ${nextContractNumber.lastDealStartDate}, ${nextContractNumber.lastDealUserName}`}
                    </div>}
                    <div className="modal-body editModal-bodyTable">
                        {isModalLoading ? (
                            <Loader/>
                        ) : (
                            <table className="table hover">
                                <thead className="editModal-table-head">
                                <tr>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                </tr>
                                </thead>
                                <tbody className="editModal-table-body">
                                {fieldArray.fields.map((position, idx) => {
                                    const fieldPosition = state.positions[idx];
                                    const initialPurityIdNamePrice = fieldPosition?.purityId && purityWithPrices
                                        ? purityWithPrices.find(element => element.value === fieldPosition?.purityId)
                                        : null;
                                    const decimalPlaces = position?.purityType === 'Серебро' ? 1 : 2;
                                    return (<Fragment key={position.positionIds}>
                                            <tr>
                                                <td colSpan="2">
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <label
                                                                className="form-element-label form-element-label__margin"
                                                                htmlFor="puritiesComment">
                                                                <span>{`${idx + 1}`}</span> Наименование
                                                            </label>
                                                            <div className="form-element-data combobox-md">
                                                                <DSelect
                                                                    name={`positions[${idx}].names`}
                                                                    defaultValue={position.names}
                                                                    options={position?.isPart ? part : jewel}
                                                                    handleOptions={handleJewelChange}
                                                                    placeholder="Название"
                                                                    isSearchable={true}
                                                                    isLoading={false}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <HInput
                                                        type="hidden"
                                                        name={`positions[${idx}].purityPrice`}
                                                        defaultValue={position.purityPrice}
                                                    />
                                                </td>
                                                {accessFeatures['purities.additional_info'] && <td colSpan="4">
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <div className="form-element__titleSpacer">
                                                                <label
                                                                    className="form-element-label"
                                                                    htmlFor="puritiesComment">
                                                                    Доп. описание
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    className="button-icontrigger"
                                                                    onClick={() => {
                                                                        if (fieldArray.fields.length !== 1) {
                                                                            removePurityRow(idx);
                                                                        }
                                                                    }}
                                                                >

                                          <span className="icon">
                                            <DeleteIcon/>
                                          </span>
                                                                </button>
                                                            </div>
                                                            <div className="form-element-data textarea ">
                                                                <HTextarea
                                                                    className="input"
                                                                    rows="1"
                                                                    name={`positions[${idx}].additionalInfo`}
                                                                    defaultValue={position.additionalInfo}
                                                                    onChange={() => handleCharCountChange(idx)}
                                                                />
                                                                {
                                                                    accessFeatures['count_additionalInfo_symbols'] &&
                                                                    <span>{charCounts[idx] !== undefined ? charCounts[idx] : position.additionalInfo.length} / 20</span>

                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>}
                                            </tr>
                                            <tr>
                                                <td colSpan="2" className='noneMarginBottom'>
                                                    <div className="table-cellcontent nonCashItem">
                                                        <div className="form-element">
                                                            <HCheckbox
                                                                h="true"
                                                                name={`positions[${idx}].isPart`}
                                                                className="input"
                                                                defaultValue={position?.isPart}
                                                                onChange={() => handlePart(idx)}
                                                            />
                                                        </div>
                                                        <label className="form-element-label m-r-spacer"
                                                               htmlFor="isPart">
                                                            Часть
                                                        </label>
                                                    </div>
                                                </td>
                                                <td colSpan="4" className='noneMarginBottom'>
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <HCheckbox
                                                                h="true"
                                                                name={`positions[${idx}].showComment`}
                                                                className="input"
                                                                defaultValue={position.showComment}
                                                                onChange={handleChangeContent}
                                                            />
                                                        </div>
                                                        <label
                                                            className="form-element-label m-r-spacer" htmlFor="comment">
                                                            Вставка
                                                        </label>
                                                    </div>
                                                    <div className="form-element">
                                                        <div className={`form-element-data textarea full-width ${
                                                            position?.showComment ? '' : 'd-none'
                                                        }`}>
                                                            <HTextarea
                                                                className="input"
                                                                rows="1"
                                                                name={`positions[${idx}].comment`}
                                                                defaultValue={position?.comment}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <label
                                                                className="form-element-label"
                                                                htmlFor="puritiesComment">
                                                                Проба
                                                            </label>
                                                            <div className="form-element-data combobox-md">
                                                                <DSelect
                                                                    name={`positions[${idx}].purityIdNamePrice`}
                                                                    isMulti={false}
                                                                    defaultValue={initialPurityIdNamePrice}
                                                                    handleChangeContent={() => handlePurityIdNamePriceChange(idx)}
                                                                    options={purityWithPrices}
                                                                    placeholder="Проба"
                                                                    isSearchable={true}
                                                                    isLoading={false}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <label
                                                                className="form-element-label"
                                                                htmlFor="puritiesComment">
                                                                Цена за грамм
                                                            </label>
                                                            <p className="editModal-pos">
                                                                {position?.purityPrice} руб./г.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <label
                                                                className="form-element-label"
                                                                htmlFor="puritiesComment">
                                                                Количество
                                                            </label>
                                                            <div className="form-element-data number">
                                                                <HInput
                                                                    name={`positions[${idx}].quantity`}
                                                                    defaultValue={position.quantity}
                                                                    className="input"
                                                                    type="text"
                                                                    decimals={0}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <label
                                                                className="form-element-label"
                                                                htmlFor="puritiesComment">
                                                                Общий вес, г.
                                                            </label>
                                                            <div className="form-element-data number">
                                                                <HInput
                                                                    name={`positions[${idx}].grossWeight`}
                                                                    defaultValue={position.grossWeight}
                                                                    onBlur={() => handleGrossWeight(idx)}
                                                                    className="input"
                                                                    type="text"
                                                                    decimals={decimalPlaces}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <label
                                                                className="form-element-label"
                                                                htmlFor="puritiesComment">
                                                                Лигатурный вес, г.
                                                            </label>
                                                            <div className="form-element-data number">
                                                                <HInput
                                                                    name={`positions[${idx}].pureWeight`}
                                                                    defaultValue={position.pureWeight}
                                                                    onBlur={() => handlePureWeight(idx, decimalPlaces)}
                                                                    className="input"
                                                                    type="text"
                                                                    minus={true}
                                                                    decimals={decimalPlaces}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="table-cellcontent">
                                                        <div className="form-element">
                                                            <label
                                                                className="form-element-label"
                                                                htmlFor="puritiesComment">
                                                                Оценочная стоимость
                                                            </label>
                                                            <p className="editModal-pos">
                                                                {fieldPosition.estimatedValue && fieldPosition.estimatedValue.toFixed(2)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr className="editModal-table-body"></tr>
                                        </Fragment>
                                    );
                                })
                                }
                                </tbody>
                                {type && (
                                    <tfoot className="editModal-table-foot">
                                    <tr>
                                        <td colSpan="5">
                                            <p>Общая оценочная стоимость</p>
                                        </td>
                                        <td colSpan="1">
                                            <p>{state.totalEstimatedValue && state.totalEstimatedValue.toFixed(2)}</p>
                                        </td>
                                    </tr>
                                    </tfoot>
                                )}
                            </table>
                        )}
                        <div className="button-position buttonPositionEditModal">
                            <button
                                className="action-item"
                                type="button"
                                onClick={checkValidLength}
                            >
                                Добавить ценность
                            </button>
                        </div>
                        {calculationError?.startsWith('Купон') && <Message type="error" text={calculationError}/>}
                        <div>
                            <fieldset>
                                <div className="form-row">
                                <div className={`${isNonCashOperationCrediton && !typeOperation ? 'flex-column' : 'd-flex'}`}>
                                    <div className="form-row-item">
                                        <div className={`form-element calculationLayout-element-width ${isNonCashOperationCrediton && 'editModal-selector'}`}>
                                            <label className="form-element-label" htmlFor="amount">
                                                Категория
                                            </label>
                                            <div className="form-element-data number">
                                                <DSelect
                                                    name={`coupon`}
                                                    isMulti={false}
                                                    defaultValue={initialCoupon}
                                                    handleChangeContent={handleCouponChange}
                                                    options={filterCoupons}
                                                    isSearchable={true}
                                                    isLoading={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        {!isNonCashOperationCrediton && <div className="form-element calculationLayout-element-width nonCashItem">
                                            <label className={(!typeOperation && !isNonCashOperationCrediton)
                                                ? "form-element-label editModal-nameInput"
                                                : "form-element-label"}
                                                   htmlFor="amount"
                                            >
                                                {(!typeOperation && !isNonCashOperationCrediton) ? 'НАЛИЧНЫЕ' : 'Сумма, руб.'}
                                            </label>
                                            <div className="form-element-data number">
                                                <HInput
                                                    type="number"
                                                    className="input"
                                                    name="calculation.deposit.cash"
                                                    onChange={handleSumChange}
                                                    defaultValue={0}
                                                    h={true}
                                                    def
                                                    step={0.01}
                                                />
                                                <span
                                                    className="error-message">{calculationError && calculationError.startsWith('Сумма') ? calculationError : ''}</span>
                                            </div>
                                        </div>} 
                                        {isNonCashOperationCrediton && typeOperation && 
                                            <div className="form-element calculationLayout-element-width nonCashItem">
                                                <label className={"form-element-label"}
                                                    htmlFor="amount"
                                                >
                                                    Сумма, руб.
                                                </label>
                                                <div className="form-element-data number">
                                                    <HInput
                                                        type="number"
                                                        className="input"
                                                        name="calculation.deposit.cash"
                                                        onChange={handleSumChange}
                                                        defaultValue={0}
                                                        h={true}
                                                        def
                                                        step={0.01}
                                                    />
                                                    <span
                                                        className="error-message">{calculationError && calculationError.startsWith('Сумма') ? calculationError : ''}</span>
                                                </div>
                                            </div>}
                                        {
                                            (!typeOperation && !isNonCashOperationCrediton) &&
                                            <>
                                                <div
                                                    className="form-element calculationLayout-element-width nonCashItem">
                                                    <label className="form-element-label editModal-nameInput"
                                                           htmlFor="amount">
                                                        БЕЗНАЛИЧНЫЕ
                                                    </label>
                                                    <div className="form-element-data number">
                                                        <HInput
                                                            type="number"
                                                            className="input"
                                                            name="calculation.deposit.nonCash"
                                                            defaultValue={0}
                                                            onChange={handleSumChange}
                                                            h={true}
                                                            def
                                                            step={0.01}
                                                        />
                                                        <span
                                                            className="error-message">{calculationError.startsWith('Сумма') ? calculationError : ''}</span>
                                                    </div>
                                                </div>
                                                <div
                                                    className="form-element calculationLayout-element-width nonCashItem">
                                                    <label className="form-element-label editModal-nameInput"
                                                           htmlFor="amount">
                                                        ЕРИП
                                                    </label>
                                                    <div className="form-element-data number">
                                                        <HInput
                                                            type="number"
                                                            className="input"
                                                            name="calculation.deposit.bankTransaction"
                                                            defaultValue={0}
                                                            onChange={handleSumChange}
                                                            h={true}
                                                            def
                                                            step={0.01}
                                                        />
                                                        <span
                                                            className="error-message">{calculationError.startsWith('Сумма') ? calculationError : ''}</span>
                                                    </div>
                                                </div>
                                            </>
                                        }
                                    </div>
                                    <div className="form-row-item">
                                        <div className="form-element calculationLayout-element">
                                            <label className="form-element-label" htmlFor="endDate">
                                                Дата
                                            </label>
                                            <div className="form-element-data date">
                                                <HDatePicker
                                                    h="true"
                                                    name="calculation.endDate"
                                                    onInput={handleCalculating}
                                                    className="input"
                                                    id="endDate"
                                                    min={minDate}
                                                    max={maxDate}
                                                    defaultValue={clientContract.endDate}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-row-item m-t-spacer-2 p-t-spacer-half">
                                        <div className="form-element calculationLayout-element m-b-0">
                                            {isDebtLoadCalculation && (
                                                <button
                                                    className="button"
                                                    type="button"
                                                    onClick={handleOpenDebtLoadModal}
                                                >
                                                    Рассчитать ПДН
                                                </button>
                                                )}
                                        </div>
                                    </div>
                                    {
                                        accessFeatures["ticketLost"] &&
                                        <div className="form-row-item calculationLayout-element">
                                            <div className="form-element calculationLayout-element">
                                                <label className="form-element-label" htmlFor="waiverOfRedemption">
                                                    Утеря квитанции
                                                </label>
                                                <div className="form-element-data">
                                                    <HCheckbox
                                                        h="true"
                                                        name="calculation.ticketLost"
                                                        className="input"
                                                        id="waiverOfRedemption"
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                                {isNonCashOperationCrediton && !typeOperation &&
                                    <div className="form-row-item editModal-selector">
                                         <PaymentTypeSelector 
                                            form={form}
                                            inputFieldName={'calculation.deposit'}
                                            handleSumChange={handleSumChange}
                                            setPaymentInputs={setPaymentInputs} />
                                    </div>}
                                </div>
                                {
                                    (!typeOperation) &&
                                    <div className='editModal-totalSum'>
                                        <h3>Итого внесено:</h3>
                                        <h3>{total.toFixed(2) || 0}</h3>
                                    </div>
                                }
                                <div className="form-row calculationLayout-row__addition">
                                    <div className="form-row-item">
                                        <div className="form-element calculationLayout-element">
                                            <div className="form-element-data static form-row-item__min-width">
                                                <p>
                                                    {(accessFeatures['coupons.birthday.enable']
                                                        || accessFeatures['coupons.bonus.enable'])
                                                    && calculations.rowName}
                                                </p>
                                                <p>Максимальная сумма микрозайм {calculations.maxLoanAmount &&
                                                    calculations.maxLoanAmount.toFixed(2)} руб.
                                                </p>
                                                <p>
                                                    Процент за пользование {calculations.basePercent}%.
                                                    Процент за неустойку {calculations.delayPercent}%
                                                </p>
                                                <p>
                                                    Переплата за день {calculations.overpaymentPerDay &&
                                                    calculations.overpaymentPerDay.toFixed(2)}
                                                    руб. /
                                                    весь срок {calculations.overpaymentTotal &&
                                                    calculations.overpaymentTotal.toFixed(2)} руб.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-element calculationLayout-element">
                                        <div className="form-row-item">
                                            <p>
                                                Дата залога {moment().format('DD.MM.YYYY')}</p>
                                            <p>
                                                Дата выкупа {moment(calculations.date).format('DD.MM.YYYY')}
                                            </p>
                                            <p>
                                                Количество дней {calculations.days === '-' ? '0' : calculations.days}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="collection">
                                    <div className="form-row">
                                        <div className="form-row-item">
                                            <div className="form-element calculationLayout-element collection-item">
                                                <div className="form-element-data static">
                                                    <p className="big">
                                                        Доступно
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-row-item">
                                            <p className="big font-bold">
                                                {calculations.availableAmount && calculations.availableAmount.toFixed(2)} руб.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-row-item">
                                            <div className="form-element calculationLayout-element collection-item">
                                                <div className="form-element-data static">
                                                    <p className="big">
                                                        Погашено микрозайма
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-row-item">
                                            <p className="big font-bold">
                                                {calculations.prevDealAmount && calculations.prevDealAmount.toFixed(2)} руб.
                                            </p>
                                        </div>
                                    </div>
                                    {calculations?.prevDealTotalOverpayment !== 0 && (
                                        <div className="form-row">
                                            <div className="form-row-item">
                                                <div className="form-element calculationLayout-element collection-item">
                                                    <div className="form-element-data static">
                                                        <p className="big">
                                                            Проценты за пользование микрозаймом
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-row-item">
                                                <p className="big font-bold">
                                                    {calculations.prevDealTotalOverpayment &&
                                                        calculations.prevDealTotalOverpayment.toFixed(2)
                                                    } руб.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {calculations?.prevDealInterestAmount !== 0 &&
                                        <div className="form-row">
                                            <div className="form-row-item">
                                                <div className="form-element calculationLayout-element collection-item">
                                                    <div className="form-element-data static">
                                                        <p className="big">
                                                            Проценты по договору микрозайма
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-row-item">
                                                <p className="big font-bold">
                                                    {calculations?.prevDealInterestAmount && calculations?.prevDealInterestAmount.toFixed(2)} руб.
                                                </p>
                                            </div>
                                        </div>
                                    }
                                    {calculations?.prevDealPenaltyAmount !== 0 &&
                                        <div className="form-row">
                                            <div className="form-row-item">
                                                <div className="form-element calculationLayout-element collection-item">
                                                    <div className="form-element-data static">
                                                        <p className="big">
                                                            Неустойка по договору микрозайма
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-row-item">
                                                <p className="big font-bold">
                                                    {calculations?.prevDealPenaltyAmount && calculations?.prevDealPenaltyAmount.toFixed(2)} руб.
                                                </p>
                                            </div>
                                        </div>
                                    }
                                    {calculations.recalculation ? (
                                        <div className="form-row">
                                            <div className="form-row-item">
                                                <div className="form-element calculationLayout-element collection-item">
                                                    <div className="form-element-data static">
                                                        <p className="big">
                                                            Перерасчет по микрозайму
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="form-row-item">
                                                <p className="big font-bold font-color-red">
                                                    {calculations.recalculation.toFixed(2)} руб.
                                                </p>
                                            </div>
                                        </div>
                                    ) : null
                                    }
                                    {calculations?.lostTicketFee !== 0 && (
                                        <div className="form-row">
                                            <div className="form-row-item">
                                                <div className="form-element calculationLayout-element collection-item">
                                                    <p className="big">
                                                        Выдача дубликата утерянного билета
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="form-row-item">
                                                <p className="big font-bold">
                                                    {calculations.lostTicketFee && calculations.lostTicketFee.toFixed(2)} руб.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-row">
                                        <div className="form-row-item">
                                            <div className="form-element calculationLayout-element collection-item">
                                                <div className="form-element-data static">
                                                    <p className="big">
                                                        Выдача микрозайма
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-row-item">
                                            <p className="big font-bold">
                                                {calculations.amount && calculations.amount.toFixed(2)} руб.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-row-item">
                                            <div className="form-element calculationLayout-element collection-item">
                                                <div className="form-element-data static">
                                                    <p className="big">
                                                        Сумма к возврату через
                                                        - {calculations.days === '-' ? '0' : calculations.days} дней
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-row-item">
                                            <p className="big font-bold">
                                                {calculations.refundAmount && calculations.refundAmount.toFixed(2)} руб.
                                            </p>
                                        </div>
                                    </div>
                                    {isDebtCalculateResult && (
                                        <div className="form-row">
                                            <div className="form-row-item fullwidth">
                                                <div className="form-element calculationLayout-element collection-item">
                                                    <DebtResults
                                                        debtLoadCalculation={debtLoadCalculation}
                                                        calculateError={debtCalculateError}
                                                        debtLoadCalculationLoading={debtLoadCalculationLoading}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-row highlighting p-t-spacer-half p-b-half">
                                        <div className="form-row-item">
                                            <div className="form-element calculationLayout-element collection-item">
                                                <div className="form-element-data static">
                                                    <p className="big font-bold">
                                                        {operationWithMoney.startsWith('Выдать') ? 'Выдать клиенту' : 'Принять от клиента'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-row-item">
                                            <p className="big font-bold m-b-0">
                                                {calculations.creditAmount ? calculations.creditAmount.toFixed(2) : '0.00'} руб.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                    </div>
                    <div className="modal-footer clientContract-modal-footer">
                        <ActionButton
                            text={operationWithMoney}
                            styleName={buttonStyle}
                            disabled={isDisabled}
                            submit
                        />
                        <ActionButton
                            text="Отменить"
                            func={handleOpenClose}
                            styleName="button-light"
                            disabled={isModalLoading}
                        />
                    </div>
                    <ConfirmModal
                        isOpen={confirmOpen.isDismissButton}
                        handleClose={handleLocalCloseModal}
                        handleAction={closePopUp}
                        headerText="Отменить изменения договора?"
                        bodyText="Внесённые данные не сохранятся."
                    />
                    <ConfirmModal
                        isOpen={confirmOpen.isApplyButton}
                        handleClose={handleLocalCloseModal}
                        handleAction={applyChanges}
                        headerText={modalHeaderText()}
                        bodyText="Внесённые данные сохранятся."
                    />
                    <BasicAlert onClose={() => !isSubmitDisabled && setErrorModalState(false)}
                                message={basicAlertMessage}
                                isOpen={errorModalState}
                                isError={!isSubmitDisabled}
                    />
                </form>
                <DebtLoadDataForm
                    isOpen={isDebtLoadData}
                    onClose={handleCloseDebtLoadModal}
                    currentDealAmount={calculations?.amount}
                    clientContract={clientContract}
                    />

            </FormProvider>

        </ReactModal>
    );

});

export default PartialQuenchingModal;
